import { Fragment, useEffect, useLayoutEffect, useRef, useState, type PointerEvent } from "react";
import { TOUCH_OPACITY_MAX, TOUCH_SIZE_SCALE, TOUCH_STEER_FEEL, type TouchSettings } from "../game/settings";
import { touchLayout, type TouchButton, type TouchKey, type TouchLayout, type TouchPad, type TouchView } from "../game/touch";
import type { TouchHints } from "../game/touch-hints";
import { TUTORIAL_STEPS, type TouchTutorial } from "../game/touch-tutorial";
import { PANEL } from "./theme";

/** Xbox colors from the palette (no green in it, so aqua for A), so the diamond reads at a glance. */
const TINT: Record<TouchButton, string> = {
  ollie: "bg-aqua/60",
  flip: "bg-accent-soft/60",
  grab: "bg-danger-soft/60",
  grind: "bg-highlight/60",
  spinL: "bg-white/45",
  spinR: "bg-white/45",
  revert: "bg-white/45",
  nollie: "bg-white/45",
  stepOff: "bg-white/45",
};

const NO_VIEW: TouchView = { stick: null, held: new Set() };

/** What lights the buttons up: the skater's `TouchHints` plus a full special meter. */
export type TouchGlow = TouchHints & { specialFull: boolean };

/** The special tricks are face-button combos, so a full meter lights these. */
const SPECIAL_KEYS: ReadonlySet<TouchButton> = new Set(["flip", "grab", "grind"]);

/** Why a key glows right now, if it does. */
function glowOf(button: TouchButton, glow: TouchGlow | undefined): "rail" | "revert" | "special" | null {
  if (!glow) return null;
  if (button === "grind" && glow.grindReady) return "rail";
  if (button === "revert" && glow.revertReady) return "revert";
  if (glow.specialFull && SPECIAL_KEYS.has(button)) return "special";
  return null;
}

/** The ring around Ollie that fills as the crouch charges; gold when full. */
function ChargeRing({ k, charge }: { k: TouchKey; charge: number }) {
  const r = k.r + 6;
  const c = 2 * Math.PI * r;
  const size = 2 * (r + 4);
  return (
    <svg
      data-ollie-charge={charge}
      aria-hidden="true"
      className="pointer-events-none absolute -rotate-90"
      style={{ left: k.x - size / 2, top: k.y - size / 2, width: size, height: size }}
      viewBox={`0 0 ${size} ${size}`}
    >
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-ink)" strokeOpacity={0.35} strokeWidth={6} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={charge >= 1 ? "var(--color-highlight)" : "white"}
        strokeWidth={6}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - charge)}
      />
    </svg>
  );
}

/** How often the tutorial's clock ticks, in ms. */
const TUTORIAL_TICK = 250;

/**
 * The first-time tutorial's current step: a pulsing ring round what it's
 * about and a card at the top middle (clear of the skater), with Skip. The card's the
 * one thing on the overlay that isn't a control, so it keeps its own taps.
 */
function TutorialCard({
  tutorial,
  layout,
  onSkip,
}: {
  tutorial: TouchTutorial;
  layout: TouchLayout;
  onSkip: () => void;
}) {
  const step = tutorial.step;
  if (!step) return null;
  const R = layout.stickRing;
  const key = layout.keys.find((k) => k.button === step.target);
  const ring =
    step.target === "stick"
      ? { x: (layout.stickMinX + layout.stickMaxX) / 2, y: layout.keys[0]?.y ?? 0, r: R + 6 }
      : key
        ? { x: key.x, y: key.y, r: key.r + 10 }
        : null;
  return (
    <>
      {ring && (
        <div
          data-tutorial-target={step.target}
          aria-hidden="true"
          className="pointer-events-none absolute animate-pulse rounded-full border-4 border-highlight ring-4 ring-highlight/50"
          style={{ left: ring.x - ring.r, top: ring.y - ring.r, width: 2 * ring.r, height: 2 * ring.r }}
        />
      )}
      <div
        role="status"
        aria-label="Touch tutorial"
        data-touch-tutorial={step.id}
        className={`${PANEL} absolute left-1/2 top-[max(1rem,env(safe-area-inset-top))] w-[min(20rem,44vw)] -translate-x-1/2 px-4 py-2 text-center`}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="text-[11px] font-black uppercase tracking-widest opacity-60">
          How to skate {tutorial.index + 1}/{TUTORIAL_STEPS.length}
        </div>
        <div className="text-sm font-black uppercase italic leading-tight">{step.text}</div>
        <button
          type="button"
          onClick={onSkip}
          className="mt-1 rounded-full border-2 border-line px-3 text-center text-xs font-bold hover:bg-surface-soft"
        >
          Skip
        </button>
      </div>
    </>
  );
}

/**
 * The on-screen stick and buttons for phones (#67). It covers the park and
 * takes every finger (no scrolling or zooming); `TouchPad` turns them into
 * the same input a pad gives. `nollie` lights the Nollie key while the next
 * ollie will be one. `settings` (Pause > Settings) sizes, fades and mirrors it.
 * `glow` lights keys when they're useful: a charge ring round Ollie while
 * crouching, Grind near a rail, Revert in its window, the special keys when
 * the meter's full. `tutorial`, the first time, walks through the stick and
 * the buttons and calls `onTutorialDone` when it's finished or skipped.
 * `only` shows just those keys (the hill's, #198), each where it always is.
 */
export function TouchControls({
  pad,
  nollie = false,
  settings,
  glow,
  tutorial = null,
  onTutorialDone,
  only,
}: {
  pad: TouchPad;
  nollie?: boolean;
  settings?: Pick<TouchSettings, "size" | "opacity" | "left"> & Partial<Pick<TouchSettings, "steer">>;
  glow?: TouchGlow;
  tutorial?: TouchTutorial | null;
  onTutorialDone?: () => void;
  only?: ReadonlySet<TouchButton>;
}) {
  const scale = TOUCH_SIZE_SCALE[settings?.size ?? "M"];
  const left = settings?.left ?? false;
  const steer = TOUCH_STEER_FEEL[settings?.steer ?? "medium"];
  const opacity = (settings?.opacity ?? TOUCH_OPACITY_MAX) / TOUCH_OPACITY_MAX;
  const ref = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState(() => pad.layout);
  const [view, setView] = useState<TouchView>(NO_VIEW);
  // Bumped when the tutorial moves on, so the card redraws.
  const [, setTutorialStep] = useState(tutorial?.index ?? 0);
  const tutorialOn = !!tutorial && !tutorial.done;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      const device = Math.min(window.screen?.width || 0, window.screen?.height || 0) || undefined;
      const next = touchLayout(el.clientWidth || window.innerWidth, el.clientHeight || window.innerHeight, { scale, left, only, steer, device });
      pad.layout = next;
      setLayout(next);
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [pad, scale, left, only, steer]);

  // Hidden (pause, a menu, a pad connected): let go of everything, or the skater keeps crouching.
  useEffect(() => () => pad.clear(), [pad]);

  const doneRef = useRef(onTutorialDone);
  doneRef.current = onTutorialDone;
  const tutorialMoved = () => {
    if (!tutorial) return;
    setTutorialStep(tutorial.index);
    if (tutorial.done) doneRef.current?.();
  };

  useEffect(() => {
    if (!tutorial || !tutorialOn) return;
    // Measured, not counted: a busy frame can hold an interval back.
    let last = performance.now();
    const t = setInterval(() => {
      const now = performance.now();
      const moved = tutorial.tick(now - last);
      last = now;
      if (!moved) return;
      setTutorialStep(tutorial.index);
      if (tutorial.done) doneRef.current?.();
    }, TUTORIAL_TICK);
    return () => clearInterval(t);
  }, [tutorial, tutorialOn]);

  /** Every finger event: redraw, and let the tutorial see what the fingers did. */
  const touched = () => {
    const v = pad.view();
    setView(v);
    if (tutorialOn && tutorial.touch(v)) tutorialMoved();
  };

  const at = (e: PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top] as const;
  };
  const down = (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pad.down(e.pointerId, ...at(e));
    touched();
  };
  const move = (e: PointerEvent<HTMLDivElement>) => {
    pad.move(e.pointerId, ...at(e));
    touched();
  };
  const up = (e: PointerEvent<HTMLDivElement>) => {
    pad.up(e.pointerId);
    touched();
  };

  const { stick, held } = view;
  const R = layout.stickRing;
  return (
    <div
      ref={ref}
      role="group"
      aria-label="Touch controls"
      data-touch-controls
      className="absolute inset-0 touch-none select-none [-webkit-touch-callout:none] [-webkit-user-select:none]"
      style={opacity < 1 ? { opacity } : undefined}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      onContextMenu={(e) => e.preventDefault()}
    >
      {stick ? (
        <>
          <div
            className="pointer-events-none absolute rounded-full border-[3px] border-ink/50 bg-white/20"
            style={{ left: stick.ox - R, top: stick.oy - R, width: 2 * R, height: 2 * R }}
          />
          <div
            data-stick-knob
            className="pointer-events-none absolute rounded-full border-[3px] border-ink bg-white/70"
            style={{ left: stick.kx - 0.45 * R, top: stick.ky - 0.45 * R, width: 0.9 * R, height: 0.9 * R }}
          />
        </>
      ) : (
        // Where the stick goes: a faint ring with a hint, until the thumb lands.
        <div
          className="pointer-events-none absolute grid place-items-center rounded-full border-[3px] border-dashed border-ink/35 text-center text-[11px] font-black uppercase italic leading-tight text-ink/60"
          style={{ left: (layout.stickMinX + layout.stickMaxX) / 2 - R, top: (layout.keys[0]?.y ?? 0) - R, width: 2 * R, height: 2 * R }}
        >
          Drag
          <br />
          to steer
        </div>
      )}
      {layout.keys.map((k) => {
        const on = held.has(k.button);
        const lit = k.button === "nollie" && nollie;
        const why = glowOf(k.button, glow);
        const charge = k.button === "ollie" ? (glow?.ollieCharge ?? 0) : 0;
        return (
          <Fragment key={k.button}>
            {charge > 0 && <ChargeRing k={k} charge={charge} />}
            <div
              data-touch-key={k.button}
              data-held={on || undefined}
              data-glow={why ?? undefined}
              className={`pointer-events-none absolute grid place-items-center rounded-full border-[3px] border-ink text-center font-black uppercase italic leading-none tracking-tighter text-ink transition-transform duration-75 ${
                lit ? "bg-highlight" : TINT[k.button]
              } ${
                on
                  ? "scale-115 opacity-100 ring-4 ring-white/70"
                  : why
                    ? `opacity-100 ring-4 ring-highlight${why === "revert" ? " animate-pulse" : ""}`
                    : "opacity-80"
              }`}
              style={{
                left: k.x - k.r,
                top: k.y - k.r,
                width: 2 * k.r,
                height: 2 * k.r,
                fontSize: Math.max(9, k.r * (k.label.length > 3 ? 0.34 : 0.6)),
              }}
            >
              {k.label}
            </div>
          </Fragment>
        );
      })}
      {tutorialOn && (
        <TutorialCard
          tutorial={tutorial}
          layout={layout}
          onSkip={() => {
            tutorial.skip();
            tutorialMoved();
          }}
        />
      )}
    </div>
  );
}
